import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E5E5E5]">
      <div className="max-w-3xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button
            data-testid="terms-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 mono-font text-xs text-[#52525B] hover:text-[#17B352] transition-colors uppercase tracking-wider mb-12"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>

          <h1 className="text-4xl md:text-5xl mb-4 italic">Terms of Service</h1>
          <p className="mono-font text-xs text-[#52525B] tracking-wider uppercase mb-12">
            Last updated: February 2026
          </p>

          <div className="space-y-10 text-[#A1A1AA] leading-relaxed">
            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using the Graphikpro website and services, you agree to be bound by these
                Terms of Service. If you do not agree to these terms, please do not use our services.
                Graphikpro Private Limited ("Graphikpro", "we", "us") provides media localization,
                subtitling, and dubbing services to clients worldwide.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">2. Services</h2>
              <p>Graphikpro offers the following services, subject to individual project agreements:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Subtitling and closed captioning in 50+ languages</li>
                <li>Dubbing and voice-over services</li>
                <li>Quality control and compliance review</li>
                <li>Multi-language content management</li>
                <li>Translation and transcription services</li>
              </ul>
              <p className="mt-3">
                Specific deliverables, timelines, and pricing are governed by individual service agreements
                or statements of work entered into between Graphikpro and the client.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">3. Client Responsibilities</h2>
              <p>As a client, you agree to:</p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Provide accurate and complete source materials for localization</li>
                <li>Ensure you hold necessary rights to content submitted for localization</li>
                <li>Provide timely feedback and approvals as outlined in project agreements</li>
                <li>Make payments in accordance with agreed terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">4. Intellectual Property</h2>
              <p>
                All original content submitted by clients remains the property of the client. Localized
                deliverables produced by Graphikpro become the property of the client upon full payment.
                Graphikpro retains intellectual property rights over its proprietary processes, tools,
                workflows, and methodologies used in delivering services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">5. Confidentiality</h2>
              <p>
                Graphikpro treats all client materials and project details as confidential. We implement
                industry-standard security measures to protect your content. Our team members and
                translators are bound by non-disclosure agreements. We will not share, distribute, or
                use your content for any purpose other than delivering the agreed services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">6. Translator Network</h2>
              <p>
                Individuals applying to join the Graphikpro translator network agree to:
              </p>
              <ul className="list-disc list-inside mt-3 space-y-2 ml-4">
                <li>Provide accurate information about their qualifications and experience</li>
                <li>Maintain confidentiality of all project materials</li>
                <li>Deliver work that meets Graphikpro quality standards</li>
                <li>Comply with project deadlines and guidelines</li>
                <li>Not subcontract or delegate assigned work without prior approval</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">7. Payment Terms</h2>
              <p>
                Payment terms are specified in individual service agreements. Unless otherwise agreed,
                invoices are due within 30 days of issuance. Late payments may incur additional charges
                as specified in the service agreement. Graphikpro reserves the right to suspend services
                for overdue accounts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">8. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable law, Graphikpro shall not be liable for
                any indirect, incidental, special, consequential, or punitive damages arising from your
                use of our services. Our total liability for any claim shall not exceed the amount paid
                by you for the specific service giving rise to the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">9. Revisions and Quality</h2>
              <p>
                Graphikpro is committed to delivering high-quality localization services. Revision
                policies, including the number of included revision rounds and timelines, are defined
                in individual project agreements. Quality disputes should be raised within 14 days of
                deliverable receipt.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">10. Governing Law</h2>
              <p>
                These Terms of Service are governed by and construed in accordance with the laws of India.
                Any disputes arising from these terms shall be subject to the exclusive jurisdiction of
                the courts in Puducherry, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">11. Changes to Terms</h2>
              <p>
                Graphikpro reserves the right to modify these terms at any time. Changes will be posted
                on this page with an updated revision date. Continued use of our services after changes
                constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl text-[#E5E5E5] mb-4">12. Contact</h2>
              <p>
                For questions regarding these Terms of Service, please contact us at{" "}
                <a href="mailto:madhiee@graphikpro.com" className="text-[#17B352] hover:underline">
                  madhiee@graphikpro.com
                </a>
              </p>
              <p className="mt-3 text-[#52525B] text-sm">
                Graphikpro Private Limited<br />
                30, Subrayan Street, Tagore Nagar, Lawspet<br />
                Puducherry - 605008, India
              </p>
            </section>
          </div>

          <div className="mt-16 pt-8 border-t border-[#1f1f23]">
            <p className="mono-font text-xs text-[#52525B] tracking-wider">
              © {new Date().getFullYear()} Graphikpro. All rights reserved.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;
